import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { UserPlus, ShieldAlert, Key, Briefcase, Mail, Phone, UserCheck, Eye, EyeOff } from 'lucide-react';
import PasswordStrengthGuide, { getPasswordValidation } from '../../Components/common/PasswordStrengthGuide';

const Register = () => {
   const [formData, setFormData] = useState({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: 'customer_care'
   });
   const [showPassword, setShowPassword] = useState(false);
   const [showConfirmPassword, setShowConfirmPassword] = useState(false);
   const [loading, setLoading] = useState(false);
   const { register, user } = useAuth();
   const navigate = useNavigate();

   // Only admin can access this page
   if (user?.role !== 'admin') {
      return (
         <div className="min-h-screen flex items-center justify-center bg-[#0f172a] p-6">
            <div className="max-w-md w-full bg-white/5 backdrop-blur-3xl rounded-[40px] border border-white/10 p-12 text-center animate-in fade-in zoom-in-95 duration-500">
               <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-red-500/20">
                  <ShieldAlert className="text-red-500 w-10 h-10" />
               </div>
               <h2 className="text-3xl font-black text-white mb-4 tracking-tight italic">Security Protocol</h2>
               <p className="text-gray-400 font-medium leading-relaxed">
                  Your current clearance level is insufficient to initialize new system identities. Please contact the DOXEZ Security Lead.
               </p>
            </div>
         </div>
      );
   }

   const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: value
      }));
   };

   const handleSubmit = async (e) => {
      e.preventDefault();

      const validation = getPasswordValidation(formData.password);
      if (!validation.isValid) {
         toast.error(validation.firstError || 'Password does not meet security requirements');
         return;
      }

      if (formData.password !== formData.confirmPassword) {
         toast.error('Identity Verification Failed: Passwords do not match');
         return;
      }

      setLoading(true);

      try {
         const result = await register({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            password: formData.password,
            role: formData.role
         });

         if (result.success) {
            toast.success('Identity Initialized Successfully');
            navigate('/users');
         } else {
            toast.error(result.message || 'Provisioning Failed');
         }
      } catch (error) {
         toast.error(error.response?.data?.message || 'Internal Provisioning Engine Error');
      } finally {
         setLoading(false);
      }
   };

   return (
      <div className="min-h-screen bg-[#F8FAFC] py-12 px-4 sm:px-6 lg:px-8 font-sans">
         <div className="max-w-2xl mx-auto">

            <div className="mb-10 text-center lg:text-left">
               <div className="flex items-center gap-3 mb-4 justify-center lg:justify-start">
                  <div className="w-10 h-10 bg-[#143E78] text-white rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/20">
                     <UserPlus size={20} />
                  </div>
                  <h2 className="text-3xl font-black text-[#143E78] tracking-tighter">Identity Provisioning</h2>
               </div>
               <p className="text-gray-400 font-medium italic">Create a new institutional identity for the DOXEZ ecosystem.</p>
            </div>

            <div className="bg-white rounded-[40px] shadow-2xl shadow-gray-200/50 border border-gray-100 p-8 sm:p-12">
               <form className="space-y-8" onSubmit={handleSubmit}>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">First Name</label>
                        <div className="relative">
                           <span className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-300"><UserCheck size={18} /></span>
                           <input
                              type="text"
                              name="firstName"
                              required
                              value={formData.firstName}
                              onChange={handleChange}
                              className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl text-gray-900 font-bold focus:bg-white focus:border-blue-100 focus:outline-none transition-all"
                              placeholder="John"
                           />
                        </div>
                     </div>
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Last Name</label>
                        <input
                           type="text"
                           name="lastName"
                           required
                           value={formData.lastName}
                           onChange={handleChange}
                           className="w-full px-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl text-gray-900 font-bold focus:bg-white focus:border-blue-100 focus:outline-none transition-all"
                           placeholder="Doe"
                        />
                     </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">User Email</label>
                        <div className="relative">
                           <span className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-300"><Mail size={18} /></span>
                           <input
                              type="email"
                              name="email"
                              required
                              value={formData.email}
                              onChange={handleChange}
                              className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl text-gray-900 font-bold focus:bg-white focus:border-blue-100 focus:outline-none transition-all"
                              placeholder="john@doxez.com"
                           />
                        </div>
                     </div>
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Verified Phone</label>
                        <div className="relative">
                           <span className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-300"><Phone size={18} /></span>
                           <input
                              type="tel"
                              name="phone"
                              required
                              value={formData.phone}
                              onChange={handleChange}
                              className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl text-gray-900 font-bold focus:bg-white focus:border-blue-100 focus:outline-none transition-all"
                              placeholder="+91 00000 00000"
                           />
                        </div>
                     </div>
                  </div>

                  <div className="space-y-2">
                     <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Functional Subsystem</label>
                     <div className="relative">
                        <span className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-300"><Briefcase size={18} /></span>
                        <select
                           name="role"
                           value={formData.role}
                           onChange={handleChange}
                           className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent rounded-2xl text-gray-900 font-bold focus:bg-white focus:border-blue-100 focus:outline-none transition-all appearance-none cursor-pointer"
                        >
                           <option value="admin">System Admin (Full Access)</option>
                           <option value="customer_care">Customer Care (Case Screening)</option>
                           <option value="cc_head">Customer Care Head (Supervisor)</option>
                           <option value="accountant">Accountant (Financial Subsystem)</option>
                           <option value="marketing_agent">Marketing (Lead Acquisition)</option>
                        </select>
                     </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">
                           System Password <span className="text-rose-500">*</span>
                        </label>
                        <div className="relative">
                           <span className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-300"><Key size={18} /></span>
                           <input
                              type={showPassword ? 'text' : 'password'}
                              name="password"
                              required
                              value={formData.password}
                              onChange={handleChange}
                              className="w-full pl-14 pr-12 py-4 bg-gray-50 border-2 border-transparent rounded-2xl text-gray-900 font-bold focus:bg-white focus:border-blue-100 focus:outline-none transition-all"
                              placeholder="••••••••"
                           />
                           <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                           >
                              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                           </button>
                        </div>
                     </div>
                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">
                           Confirm Identity <span className="text-rose-500">*</span>
                        </label>
                        <div className="relative">
                           <input
                              type={showConfirmPassword ? 'text' : 'password'}
                              name="confirmPassword"
                              required
                              value={formData.confirmPassword}
                              onChange={handleChange}
                              className={`w-full px-6 pr-12 py-4 bg-gray-50 border-2 rounded-2xl text-gray-900 font-bold focus:bg-white focus:outline-none transition-all ${
                                 formData.confirmPassword && formData.password !== formData.confirmPassword
                                    ? 'border-rose-400 bg-rose-50/20'
                                    : formData.confirmPassword && formData.password === formData.confirmPassword
                                    ? 'border-emerald-400 bg-emerald-50/20'
                                    : 'border-transparent focus:border-blue-100'
                              }`}
                              placeholder="••••••••"
                           />
                           <button
                              type="button"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                           >
                              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                           </button>
                        </div>
                     </div>
                  </div>

                  {/* Password Requirements Guide */}
                  <PasswordStrengthGuide password={formData.password} />

                  <button
                     type="submit"
                     disabled={loading}
                     className="w-full bg-[#143E78] text-white py-5 rounded-2xl font-black text-sm uppercase tracking-[0.2em] shadow-2xl shadow-blue-900/20 hover:bg-[#0f2c56] active:scale-95 transition-all disabled:opacity-50 mt-8 flex items-center justify-center gap-3"
                  >
                     {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                     ) : (
                        <>Initialize Account <UserPlus className="w-4 h-4" /></>
                     )}
                  </button>
               </form>
            </div>
         </div>
      </div>
   );
};

export default Register;
